<template lang="pug">
    span(class="_tag", :class="{ ['bg-'+tone]: tone, ['_e'+appearance]: appearance }")
        span(class="_tagi")
            slot &zwnj;
        ib-button(
            v-if="$listeners.delete",
            type="button",
            tone="gray",
            class="link",
            tabindex="-1",
            :disabled="disabled",
            @mousedown.stop="",
            @click="onDelete")
            include ../../icon/basic/close.svg
</template>

<script>
    /**
     * A styled tag (known as a Chip in Material Design). If there is a `delete` event listener, the tag will be
     * rendered with a delete button.
     */
    export default {
        name: 'ib-tag',

        props: {
            /**
             * Semantic color class.
             */
            tone: String,
            /**
             * Appearance modifier:
             * - `arrow`: Five sided shape similar to a price tag.
             * - `box`: Four sided, slightly rounded rectangle.
             * - `large`: Similar to default appearance, but larger.
             * - `medium`: Similar to default appearance, but medium sized.
             */
            appearance: String,
            /**
             * If deletable, sets the disabled property on the delete button.
             */
            disabled: Boolean
        },

        methods: {
            /**
            * 'delete' event
            * @event
            * @type{null}
            */
            onDelete() {
                this.$emit('delete');
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._tag {
        position: relative;
        display: inline-flex;
        align-items: center;
        padding: 2px 8px 1px;
        .font-size(@fs7);
        .line-height-auto(@fs7);
        font-weight: 500;
        max-width: 100%;
        white-space: nowrap;
        border: none;
        border-radius: 99em;
        color: @fg-accent;
        background-color: fade(@bg-accent, 10%);

        &:hover {
            background-color: fade(@bg-accent, 20%);
        }
        &._elarge {
            padding: 6px 12px;
        }
        &._emedium {
            padding: 4px 10px;
        }
        &._earrow {
            margin-left: .7071em;
            border: none;
            border-radius: 0;
            padding: 1px 10px;
            -webkit-padding-start: 6px;
            padding-inline-start: 6px;
            &::before {
                content: '';
                position: absolute;
                display: inline-block;
                top: 0;
                width: calc(~'.8839em + 1px');
                height: 70.7107%;  /** 1 / sqrt(2) */
                background-color: inherit;
                z-index: -1;
            }
        }
        &._ebox {
            border-radius: 3px;
        }

        > ._btn {
            margin: 0 -6px 0 6px;
            > svg {
                width: 12px;
                height: 12px;
                vertical-align: -1.5px;
            }
        }
    }

    ._tagi {
        flex: 1 1 auto;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    [dir=ltr] ._tag,
    [dir=rtl] [dir=ltr] ._tag {
        &._earrow {
            &::before {
                left: 0;
                transform-origin: 0 0;
                transform: rotate(45deg);
            }
        }
    }

    [dir=rtl] ._tag,
    [dir=ltr] [dir=rtl] ._tag {
        &._earrow {
            &::before {
                right: 0;
                transform-origin: 100% 0;
                transform: rotate(-45deg);
            }
        }
    }
</style>

<ib-example id="basic" name="Basic">
    <template lang="pug">
        ib-tag Hello, world.
    </template>
</ib-example>

<ib-example id="delete" name="Deletable">
    <template lang="pug">
        div
            template(v-for="tag in tags")
                ib-tag(@delete="remove(tag)") {{tag}}
                | &nbsp;
    </template>
    <script>
        {
            data() {
                return {
                    tags: ['Eddard','Catelyn','Robb','Sansa','Brandon','Arya','Rickon']
                };
            },
            methods: {
                remove(tag) {
                    this.tags = this.tags.filter(e => e !== tag);
                }
            }
        }
    </script>
</ib-example>

<ib-example id="appearance" name="Appearance">
    <template lang="pug">
        ib-tag(appearance="arrow", tone="important") NEW!
    </template>
</ib-example>
